import { Link, useLocation, useNavigate } from 'react-router-dom'
import { CircleUser, LogOut } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'

import { cn } from '@/custolink-newdesign/lib/utils'
import ShadcnScope from '@/custolink-newdesign/components/ShadcnScope'
import { Avatar, AvatarFallback } from '@/custolink-newdesign/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/custolink-newdesign/ui/dropdown-menu'
import { navIcon } from '@/custolink-newdesign/shell/navIcons'
import { useNavigation } from '@/configs/useNavigation'
import useUiContext from '@/custolink-hooks/useUiContext'
import { AdmUserApi } from '@/custolink-transpiled_by_server/bc.identity_and_access.api_methods'
import type { NavigationTree } from '@/@types/navigation'
import { NAV_ITEM_TYPE_COLLAPSE } from '@/constants/navigation.constant'
import { SETTINGS_GROUP_KEY } from '@/custolink-newdesign/shell/SettingsShell'

/**
 * The navigation rail. Markup and classes are flow's NavbarContent.tsx:96-278,
 * fed by THIS app's configs/navigation.config.ts.
 *
 * One deliberate structural change: the existing rail renders Assets,
 * Communication and Settings as COLLAPSE groups that expand on click
 * (components/template/VerticalMenuContent). flow's rail has no collapsibles -
 * groups are always-open sections under an uppercase label
 * (NavbarContent.tsx:150). Matching flow 1:1 means flattening those three
 * groups, so every destination is one click away and visible.
 */

/** flow NavbarContent.tsx:33-40 */
function NavBadge({ title, isActive }: { title: string; isActive: boolean }) {
  return (
    <span
      className={cn(
        'cl:ml-auto cl:shrink-0 cl:rounded-full cl:px-1.5 cl:text-xs cl:leading-5 cl:font-semibold cl:tabular-nums',
        isActive
          ? 'cl:bg-accent cl:text-accent-foreground'
          : 'cl:bg-muted cl:text-muted-foreground'
      )}
    >
      {title}
    </span>
  )
}

/** flow NavbarContent.tsx:96-118 */
function NavItem({ item, isActive }: { item: NavigationTree; isActive: boolean }) {
  const Icon = navIcon(item.icon)

  return (
    // Link, not NavLink: the active entry is decided across the whole rail by
    // activeEntryKey, and NavLink would stamp its own prefix-matched
    // aria-current on top of that verdict.
    <Link
      to={item.path || '#'}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'cl:flex cl:min-h-9 cl:items-center cl:gap-3 cl:rounded-lg cl:px-2.5 cl:py-1.5 cl:text-sm cl:transition-[background-color,box-shadow,color] cl:duration-150',
        'cl:focus-visible:ring-ring/50 cl:outline-none cl:focus-visible:ring-[3px]',
        // The rail sits on --background; the active entry is a raised card,
        // which is the Calm rail's own idiom rather than a tinted row.
        isActive
          ? 'cl:bg-card cl:text-primary cl:font-semibold cl:shadow-sm'
          : 'cl:text-foreground cl:hover:bg-card/70 cl:font-medium cl:hover:shadow-sm'
      )}
    >
      <Icon
        className={cn(
          'cl:size-4 cl:shrink-0',
          isActive ? 'cl:text-primary' : 'cl:text-muted-foreground'
        )}
      />
      <span className="cl:truncate">{item.title}</span>
    </Link>
  )
}

/**
 * Longest matching path wins, so /admin/assets/import does not also light up
 * /admin/assets. flow does the same thing in lib/navigation/activeEntryId.
 */
function activeEntryKey(items: NavigationTree[], pathname: string): string | null {
  let best: { key: string; len: number } | null = null
  for (const it of items) {
    if (!it.path) continue
    const hit = pathname === it.path || pathname.startsWith(it.path + '/')
    if (hit && (!best || it.path.length > best.len)) best = { key: it.key, len: it.path.length }
  }
  return best?.key ?? null
}

/** Every settings route lives under this prefix. */
const SETTINGS_PREFIX = '/admin/settings'

function NavTree() {
  const { navigationConfig } = useNavigation()
  const { pathname } = useLocation()

  // Flatten COLLAPSE groups into always-open labelled sections - except
  // Settings, which collapses to ONE entry.
  //
  // SettingsShell renders its own rail of exactly these items, so listing them
  // here too puts the same four links on screen twice, side by side. flow's
  // main rail has no settings section at all for the same reason: settings is
  // its own area with its own navigation.
  const sections = navigationConfig.map((node) => {
    if (node.key === SETTINGS_GROUP_KEY) {
      const first = (node.subMenu ?? [])[0]
      return {
        label: null,
        items: first
          ? [{ ...node, path: first.path, title: node.title, icon: 'settings' }]
          : [],
      }
    }
    return node.type === NAV_ITEM_TYPE_COLLAPSE
      ? { label: node.title, items: node.subMenu ?? [] }
      : { label: null, items: [node] }
  })

  const items = sections.flatMap((s) => s.items)
  // The settings entry points at its first child, so a path match alone would
  // drop the highlight on every other settings page.
  const activeKey = pathname.startsWith(SETTINGS_PREFIX)
    ? SETTINGS_GROUP_KEY
    : activeEntryKey(items, pathname)

  return (
    <nav aria-label="Main" className="cl:flex cl:flex-col cl:gap-6 cl:px-3 cl:pt-1 cl:pb-3">
      {sections.map((section, i) => (
        <div key={section.label ?? `solo-${i}`} className="cl:flex cl:flex-col cl:gap-1">
          {section.label ? (
            // Full muted-foreground, not a faded one: at 10.5px uppercase the
            // tinted variant falls under 4.5:1 on the rail's --background.
            <div className="cl:text-muted-foreground cl:px-2.5 cl:pb-1 cl:text-[10.5px] cl:font-bold cl:tracking-[0.09em] cl:uppercase">
              {section.label}
            </div>
          ) : null}
          {section.items.map((item) => (
            <NavItem key={item.key} item={item} isActive={item.key === activeKey} />
          ))}
        </div>
      ))}
    </nav>
  )
}

/**
 * Identity card at the foot of the rail. flow NavbarContent.tsx:204-259.
 *
 * In the existing design this lives in the TOPBAR as UserProfileDropdown
 * (components/layouts/PostLoginLayout/CollapsibleSide.tsx:37). flow puts it at
 * the foot of the rail, so it moves - same actions, same mutation.
 */
function UserFooter() {
  const uic = useUiContext()
  const navigate = useNavigate()
  const signOut = useMutation({ mutationFn: () => AdmUserApi.signout() })

  const user = uic.adminAreaAuthedUser
  if (!user) return null

  const name = user.name || user.email || ''
  const subtitle = uic.organisationContext?.organisationSlug || user.email || ''

  const parts = (user.name || user.email.split('@')[0]).split(/[\s._-]+/).filter(Boolean)
  const initials = (
    parts.length > 1 ? parts[0][0] + parts[1][0] : (parts[0] ?? '?').slice(0, 2)
  ).toUpperCase()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="cl:hover:bg-card cl:focus-visible:ring-ring/50 cl:flex cl:w-full cl:items-center cl:gap-2.5 cl:rounded-xl cl:px-2 cl:py-1.5 cl:text-left cl:transition-colors cl:outline-none cl:hover:shadow-sm cl:focus-visible:ring-[3px]"
          aria-label="Account menu"
        >
          <Avatar className="cl:size-[30px]">
            <AvatarFallback className="cl:bg-accent cl:text-accent-foreground cl:text-[11px] cl:font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="cl:flex cl:min-w-0 cl:flex-col cl:leading-tight">
            <span className="cl:truncate cl:text-[13px] cl:font-semibold">{name}</span>
            <span className="cl:text-muted-foreground cl:truncate cl:text-[11px]">{subtitle}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="cl:min-w-40">
        <DropdownMenuItem onSelect={() => navigate('/admin/profile')}>
          <CircleUser />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={async () => {
            await signOut.mutateAsync()
            document.location.reload()
          }}
        >
          <LogOut />
          Sign off
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** flow NavbarContent.tsx:262-278 */
export default function NavbarContent({ className }: { className?: string }) {
  return (
    <ShadcnScope
      className={cn(
        'cl:bg-background cl:flex cl:h-full cl:flex-auto cl:flex-col cl:overflow-hidden',
        className
      )}
    >
      {/* Taller than flow's h-12/h-16 header row: this lockup is a wordmark
          plus a tagline, so it needs the height to stay legible. */}
      <div className="cl:flex cl:h-16 cl:shrink-0 cl:flex-row cl:items-center cl:px-5 cl:md:h-20">
        {/* The transparent lockup, not custolinklogo.png: that one carries an
            opaque white plate, which prints a white rectangle against the
            rail's --cl-background (#f3f4f2). */}
        <img
          src="/img/logo/custolinklogo_transparent.png"
          alt="CustoLink"
          className="cl:block cl:h-11 cl:w-auto cl:object-contain"
        />
      </div>

      <div className="cl:min-h-0 cl:flex-1 cl:overflow-x-hidden cl:overflow-y-auto cl:overscroll-contain">
        <NavTree />
      </div>

      <div className="cl:flex cl:flex-col cl:gap-3 cl:border-t cl:p-3">
        <UserFooter />
      </div>
    </ShadcnScope>
  )
}
